import {announce} from '@github-ui/aria-live'
import {PercentageCircle} from '@github-ui/percentage-circle'
import {Banner} from '@primer/react'
import {debounce} from 'lodash-es'
import {useEffect, useMemo, useRef} from 'react'

import {useBulkAddNotifications} from '../state-providers/bulk-add-notifications/bulk-add-notifications-state-provider'
import {BulkAddResources} from '../strings'
import styles from './bulk-add-notifications.module.css'

export const BulkAddNotifications = () => {
  const {bulkAddState, clearNotification} = useBulkAddNotifications()

  const {percentage, totalItemsAdded, totalItemsFailed, isComplete, initialImport} = bulkAddState ?? {}

  const bannerProps = useMemo(() => {
    let variant: 'success' | 'critical' | 'info'
    let message: string
    let icon: React.ReactNode = undefined
    let dismissible = false
    // We only want to consider the bulk add failed if no items were added at all
    const failed = totalItemsAdded === 0 && totalItemsFailed !== 0

    if (isComplete && failed) {
      variant = 'critical'
      message = BulkAddResources.failedMessage(totalItemsFailed ?? 0)
      dismissible = true
    } else if (isComplete) {
      variant = 'success'
      message = BulkAddResources.successMessage(totalItemsAdded ?? 0, totalItemsFailed ?? 0, initialImport ?? false)
      dismissible = true
    } else if (percentage !== null && percentage !== undefined && percentage > 0) {
      variant = 'info'
      message = BulkAddResources.progressMessage(percentage)
      icon = <PercentageCircle radius={7} progress={percentage / 100.0} />
    } else {
      variant = 'info'
      message = BulkAddResources.startMessage
    }

    return {variant, message, icon, dismissible}
  }, [isComplete, totalItemsAdded, totalItemsFailed, percentage, initialImport])

  const lastMessage = useRef('')

  const debounceAnnouncement = debounce(
    (announcement: string, assertive: boolean = false) => {
      announce(announcement, {assertive})
    },
    200,
    {trailing: true, leading: false},
  )

  useEffect(() => {
    if (bulkAddState && bannerProps.message !== lastMessage.current) {
      debounceAnnouncement(bannerProps.message, bannerProps.variant !== 'info')
      lastMessage.current = bannerProps.message
    }
  }, [debounceAnnouncement, bulkAddState, bannerProps])

  if (!bulkAddState) {
    return null
  }

  return (
    <Banner
      variant={bannerProps.variant}
      title={bannerProps.message}
      hideTitle
      onDismiss={bannerProps.dismissible ? clearNotification : undefined}
      className={styles.banner}
      icon={bannerProps.icon}
    >
      {bannerProps.message}
    </Banner>
  )
}
